import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages product swatches.
 */
class ProductSwatches extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.handleSwatchClick.bind(this));

    const currentProductId = this.dataset.productId;

    if (currentProductId) {
      const targetSwatch = this.querySelector(`[data-product-id="${currentProductId}"]`);
      if (targetSwatch instanceof HTMLButtonElement) {
        this.selectSwatch(targetSwatch);
        return;
      }
    }

    const alreadySelected = this.querySelector('.product-swatches__swatch--selected');
    if (alreadySelected instanceof HTMLButtonElement) {
      this.updateSelectedFlavour(alreadySelected);
      return;
    }

    const firstAvailable = this.querySelector('.product-swatches__swatch:not(.product-swatches__swatch--disabled)');
    if (firstAvailable instanceof HTMLButtonElement) {
      this.selectSwatch(firstAvailable);
      return;
    }

    const firstSwatch = this.querySelector('.product-swatches__swatch');
    if (firstSwatch instanceof HTMLButtonElement) {
      this.selectSwatch(firstSwatch);
    }
  }

  /** @param {Event} event */
  handleSwatchClick(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const swatch = event.target.closest('.product-swatches__swatch');
    if (!(swatch instanceof HTMLButtonElement)) return;

    this.selectSwatch(swatch);
    this.updateProductData(swatch);
  }

  /** @param {HTMLButtonElement} swatch */
  selectSwatch(swatch) {
    this.querySelectorAll('.product-swatches__swatch').forEach((s) => {
      s.classList.remove('product-swatches__swatch--selected');
    });

    swatch.classList.add('product-swatches__swatch--selected');
    this.updateSelectedFlavour(swatch);
  }

  /** @param {HTMLButtonElement} swatch */
  updateSelectedFlavour(swatch) {
    const flavourName = swatch.dataset.flavourName || '';
    const selectedFlavourElement = this.querySelector('.product-swatches__selected-flavour');
    if (selectedFlavourElement) {
      selectedFlavourElement.textContent = flavourName;
    }
  }

  /** @param {HTMLButtonElement} swatch */
  async updateProductData(swatch) {
    const productId = swatch.dataset.productId;
    const variantId = swatch.dataset.variantId;
    const productUrl = swatch.dataset.productUrl;
    const bgColor1 = swatch.dataset.bgColor1;
    const bgColor2 = swatch.dataset.bgColor2;
    const bgGradient = swatch.dataset.bgGradient;

    if (!productId || !variantId || !productUrl) return;

    const isOnProductPage = this.isOnProductPage();

    if (isOnProductPage && productUrl) {
      try {
        let selectedSize = null;
        const fakeVariantPicker = document.querySelector('product-metafield-variant-picker');
        if (fakeVariantPicker instanceof HTMLElement) {
          const selectedFakeVariant = fakeVariantPicker.querySelector('input[type="radio"]:checked');
          if (selectedFakeVariant instanceof HTMLInputElement) {
            const variantTitle = selectedFakeVariant.dataset.variantTitle || '';
            if (variantTitle) {
              const titleParts = variantTitle.split(' - ');
              if (titleParts.length > 1) {
                const lastPart = titleParts[titleParts.length - 1];
                if (lastPart) {
                  selectedSize = lastPart.trim();
                }
              }
            }
          }
        }

        const productSection = this.findProductSection();
        if (productSection) {
          const sectionId = productSection.id.replace('shopify-section-', '');
          const sectionUrl = new URL(productUrl, window.location.origin);
          if (variantId) {
            sectionUrl.searchParams.set('variant', variantId);
          }

          await sectionRenderer.renderSection(sectionId, {
            url: sectionUrl,
            cache: false,
          });

          requestAnimationFrame(() => {
            requestAnimationFrame(async () => {
              const urlVariantId = this.getVariantIdFromUrl();
              let targetSwatch = null;

              if (urlVariantId) {
                targetSwatch = this.querySelector(`[data-variant-id="${urlVariantId}"]`);
              }

              if (!targetSwatch && productId) {
                targetSwatch = this.querySelector(`[data-product-id="${productId}"]`);
              }

              if (targetSwatch instanceof HTMLButtonElement) {
                this.selectSwatch(targetSwatch);
              } else {
                const alreadySelected = this.querySelector('.product-swatches__swatch--selected');
                if (alreadySelected instanceof HTMLButtonElement) {
                  this.updateSelectedFlavour(alreadySelected);
                }
              }

              if (selectedSize) {
                const newFakeVariantPicker = document.querySelector('product-metafield-variant-picker');
                if (newFakeVariantPicker instanceof HTMLElement) {
                  const allRadios = newFakeVariantPicker.querySelectorAll('input[type="radio"]');
                  for (const radio of allRadios) {
                    if (radio instanceof HTMLInputElement) {
                      const radioTitle = radio.dataset.variantTitle || '';
                      if (radioTitle) {
                        const titleParts = radioTitle.split(' - ');
                        if (titleParts.length > 1) {
                          const lastPart = titleParts[titleParts.length - 1];
                          if (lastPart) {
                            const radioSize = lastPart.trim();
                            if (radioSize === selectedSize && radio.dataset.variantHasQuantity === 'true') {
                              allRadios.forEach((r) => {
                                if (r instanceof HTMLInputElement) {
                                  r.checked = false;
                                }
                              });
                              radio.checked = true;
                              const pickerElement = /** @type {any} */ (newFakeVariantPicker);
                              if (typeof pickerElement.updateProductData === 'function') {
                                newFakeVariantPicker.dataset.skipRender = 'true';
                                await pickerElement.updateProductData(radio);
                                if (newFakeVariantPicker.dataset) {
                                  delete newFakeVariantPicker.dataset.skipRender;
                                }
                              }
                              break;
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            });
          });
        }
      } catch (error) {
        console.error('Error updating product page:', error);
      }
    } else {
      this.updateProductBackground(bgColor1 || '', bgColor2 || '', bgGradient || '');
      this.updateAddToCartForm(productId, variantId);
      this.updateQuantitySelector(variantId);
    }

    if (isOnProductPage && productUrl) {
      const url = new URL(window.location.href);
      const newUrl = new URL(productUrl, window.location.origin);
      if (variantId) {
        newUrl.searchParams.set('variant', variantId);
      }
      if (url.pathname !== newUrl.pathname || url.search !== newUrl.search) {
        window.history.replaceState({}, '', newUrl.toString());
      }
    }

    this.dispatchEvent(
      new CustomEvent('product-swatch-change', {
        bubbles: true,
        detail: {
          productId: productId,
          variantId: variantId,
          url: productUrl || '',
        },
      })
    );
  }

  /**
   * Gets the variant ID from the URL query parameter
   * @returns {string | null}
   */
  getVariantIdFromUrl() {
    const url = new URL(window.location.href);
    return url.searchParams.get('variant');
  }

  isOnProductPage() {
    return !!(
      document.querySelector('media-gallery') ||
      document.querySelector('[data-testid="product-information-media"]') ||
      document.querySelector('.product-information__grid')
    );
  }

  /**
   * Finds the product information section element
   * @returns {HTMLElement | null}
   */
  findProductSection() {
    const productInfoElement = document.querySelector(
      '.product-information, [data-testid="product-information-media"], .product-information__grid'
    );
    if (productInfoElement) {
      const section = productInfoElement.closest('.shopify-section');
      if (section instanceof HTMLElement && section.id) {
        return section;
      }
    }

    const mediaGallery = document.querySelector('media-gallery');
    if (mediaGallery) {
      const section = mediaGallery.closest('.shopify-section');
      if (section instanceof HTMLElement && section.id) {
        return section;
      }
    }

    return null;
  }

  /** @param {string} color1 @param {string} color2 @param {string} gradient */
  updateProductBackground(color1, color2, gradient) {
    const productContext = this.getProductContext();
    if (!productContext) return;

    const productCard = productContext.querySelector('product-card') || productContext.closest('product-card');
    if (productCard instanceof HTMLElement) {
      if (gradient && gradient.trim() !== '') {
        if (color1) {
          productCard.style.setProperty('--product-bg-color', color1);
          productCard.dataset.color1 = color1;
        }
        if (gradient) {
          productCard.style.setProperty('--product-background-gradient', gradient);
        }
        if (color2) {
          productCard.dataset.color2 = color2;
        }
      } else {
        productCard.style.removeProperty('--product-bg-color');
        productCard.style.removeProperty('--product-background-gradient');
        delete productCard.dataset.color1;
        delete productCard.dataset.color2;
      }
    }

    const mediaGallery = document.querySelector('media-gallery');
    if (mediaGallery) {
      const productMediaContainers = mediaGallery.querySelectorAll('.product-media');
      productMediaContainers.forEach((container) => {
        if (!(container instanceof HTMLElement)) return;

        if (gradient && gradient.trim() !== '') {
          if (color1) {
            container.style.setProperty('--product-bg-color', color1);
            container.setAttribute('data-color1', color1);
          }
          if (gradient) {
            container.style.setProperty('--product-background-gradient', gradient);
          }
          if (color2) {
            container.setAttribute('data-color2', color2);
          }
          const existingStyle = container.getAttribute('style') || '';
          const hasBgColor = existingStyle.includes('--product-bg-color');
          const hasGradient = existingStyle.includes('--product-background-gradient');
          if (!hasBgColor || !hasGradient) {
            let newStyle = existingStyle;
            if (color1 && !hasBgColor) {
              newStyle += ` --product-bg-color: ${color1};`;
            }
            if (gradient && !hasGradient) {
              newStyle += ` --product-background-gradient: ${gradient};`;
            }
            container.setAttribute('style', newStyle.trim());
          }
        } else {
          container.style.removeProperty('--product-bg-color');
          container.style.removeProperty('--product-background-gradient');
          container.removeAttribute('data-color1');
          container.removeAttribute('data-color2');
        }
      });
    }
  }

  /** @param {string} productId @param {string} variantId */
  updateAddToCartForm(productId, variantId) {
    if (!productId || !variantId) return;

    const productForm = document.querySelector('product-form-component');
    if (!productForm) return;

    const variantIdInput = productForm.querySelector('input[ref="variantId"], input[name="id"]');
    if (variantIdInput instanceof HTMLInputElement) {
      variantIdInput.value = variantId;
      variantIdInput.dispatchEvent(new Event('input', { bubbles: true }));
      variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const productIdInput = productForm.querySelector('input[name="product-id"]');
    if (productIdInput instanceof HTMLInputElement) {
      productIdInput.value = productId;
    }

    if (productForm instanceof HTMLElement) {
      productForm.setAttribute('data-product-id', productId);

      const swatch = this.querySelector(`[data-product-id="${productId}"]`);
      const productUrl = swatch instanceof HTMLButtonElement ? swatch.dataset.productUrl : undefined;
      if (productUrl) {
        productForm.setAttribute('data-product-url', productUrl);
      }
    }

    productForm.dispatchEvent(
      new CustomEvent('variant-updated', {
        detail: {
          productId: productId,
          variantId: variantId,
        },
        bubbles: true,
      })
    );

    const quantitySelector = productForm.querySelector('quantity-selector-component');
    if (quantitySelector instanceof HTMLElement) {
      quantitySelector.setAttribute('data-variant-id', variantId);
      const quantityInput = quantitySelector.querySelector('input[type="number"]');
      if (quantityInput instanceof HTMLInputElement) {
        quantityInput.setAttribute('data-variant-id', variantId);
        quantitySelector.dispatchEvent(
          new CustomEvent('variant-changed', {
            detail: { variantId: variantId },
            bubbles: true,
          })
        );
      }
    }
  }

  getProductContext() {
    const productId = this.dataset.productId;

    let productCard = this.closest('product-card');
    if (productCard instanceof HTMLElement) return productCard;

    productCard = this.closest('[data-product-id]');
    if (productCard instanceof HTMLElement) return productCard;

    if (productId) {
      const cardById = document.querySelector(`[data-product-id="${productId}"]`);
      if (cardById instanceof HTMLElement) return cardById;
    }

    const productInfoElement = document.querySelector(
      '.product-information, [data-testid="product-information-media"], .product-information__grid'
    );
    if (productInfoElement) {
      const productInfo =
        productInfoElement.closest('.shopify-section') ||
        productInfoElement.closest('.product-information__grid') ||
        productInfoElement;
      if (productInfo instanceof HTMLElement) return productInfo;
    }

    const section = this.closest('.shopify-section');
    if (section instanceof HTMLElement) return section;

    return document.body;
  }

  /** @param {string} variantId */
  updateQuantitySelector(variantId) {
    const productContext = this.getProductContext();
    if (!productContext) return;

    const quantitySelectors = productContext.querySelectorAll(
      'quantity-selector-component, cart-quantity-selector-component'
    );
    quantitySelectors.forEach((selector) => {
      if (selector instanceof HTMLElement) {
        selector.setAttribute('data-variant-id', variantId);
        const quantityInput = selector.querySelector('input[type="number"]');
        if (quantityInput instanceof HTMLInputElement) {
          quantityInput.setAttribute('data-variant-id', variantId);
          selector.dispatchEvent(new CustomEvent('variant-changed', { detail: { variantId } }));
        }
      }
    });
  }
}

if (!customElements.get('product-swatches')) {
  customElements.define('product-swatches', ProductSwatches);
}
